import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { ClipboardCheck, MoreVertical } from 'lucide-react';
import { StatusBadge } from 'src/components/common/StatusBadge';
import DataTable from 'src/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { DescriptionCell } from './DescriptionCell';

const NameCell = ({ value, onEdit, id, status }) => (
  <span className="inline-flex items-center gap-2.5">
    <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
      <ClipboardCheck className="w-[15px] h-[15px]" strokeWidth={1.9} />
    </span>
    <button
      type="button"
      className="text-[13px] font-semibold text-foreground-strong text-left bg-transparent border-0 p-0 cursor-pointer hover:text-primary hover:underline disabled:cursor-default disabled:no-underline"
      onClick={() => onEdit(id)}
      disabled={status === 'ACTIVE'}
    >
      {value}
    </button>
  </span>
);

const ActionsCell = ({ row, onEdit, onPublish, onDeactivate }) => (
  <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {row.status === 'DRAFT' && (
          <DropdownMenuItem onClick={() => onPublish(row.id)}>
            Publish
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onEdit(row.id)}
          disabled={row.status === 'ACTIVE'}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDeactivate(row.id)}>
          Deactivate
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export const TestsTable = ({
  tests,
  onEdit,
  onDeactivate,
  onPublish,
  currentPage = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) => {
  const [publishDialog, setPublishDialog] = useState({ open: false, id: null });
  const [deactivateDialog, setDeactivateDialog] = useState({
    open: false,
    id: null,
  });

  const handlePublishClick = (id) => setPublishDialog({ open: true, id });

  const handlePublishConfirm = () => {
    if (publishDialog.id) onPublish(publishDialog.id);
    setPublishDialog({ open: false, id: null });
  };

  const handleDeactivateClick = (id) => setDeactivateDialog({ open: true, id });

  const handleDeactivateConfirm = () => {
    if (deactivateDialog.id) onDeactivate(deactivateDialog.id);
    setDeactivateDialog({ open: false, id: null });
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      minWidth: '200px',
      render: (v, row) => (
        <NameCell value={v} onEdit={onEdit} id={row.id} status={row.status} />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      minWidth: '280px',
      render: (v, row) => (
        <DescriptionCell
          value={v}
          label={row.name}
          maxWidthClass="max-w-[44ch]"
        />
      ),
    },
    {
      key: 'max_score',
      header: 'Max Score',
      width: '110px',
      minWidth: '80px',
      align: 'right',
      render: (v) => (
        <span className="font-mono text-[12.5px] font-semibold text-foreground-strong">
          {v ?? '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      minWidth: '110px',
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: 'actions',
      header: '',
      width: '56px',
      minWidth: '56px',
      render: (_v, row) => (
        <ActionsCell
          row={row}
          onEdit={onEdit}
          onPublish={handlePublishClick}
          onDeactivate={handleDeactivateClick}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        data={tests}
        columns={columns}
        loading={loading}
        tableLayout="auto"
        hover
        stickyHeader
        pagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={Math.max(
          1,
          Math.ceil((totalItems || 0) / (pageSize || 10))
        )}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <AlertDialog
        open={publishDialog.open}
        onOpenChange={(open) =>
          !open && setPublishDialog({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Publish</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish this test? Once published, it
              will be available for use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setPublishDialog({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handlePublishConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deactivateDialog.open}
        onOpenChange={(open) =>
          !open && setDeactivateDialog({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deactivate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate this test? It will no longer
              be available for use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeactivateDialog({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivateConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
